import { PlusOutlined } from '@ant-design/icons'
import {
  AutoComplete,
  Button,
  Card,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  type RadioChangeEvent,
  Space,
  Typography,
  Upload,
} from 'antd'
import type {
  SectionAddendum,
  SectionAddendumAttachment,
} from 'pages/reporting/reports/rug/rugReportData'
import React from 'react'
import styled from 'styled-components'
import {
  autocompleteFilterOptionWithoutExactMatch,
  autocompletePreviewDisabledProps,
} from 'utils/fde/antD'

import { AddendumAttachmentRow } from './AddendumAttachmentRow'

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`
const Container = styled(Card)`
  margin-bottom: 1rem;
`

interface Props {
  addendum: SectionAddendum
  index: number
  sectionOptions: { value: string; label: string }[]
  disabled: boolean
  urls: ReadonlyMap<string, string>
  onChange: (change: (current: SectionAddendum) => SectionAddendum) => void
  onRemove: () => void
  uploadFile: (file: File) => Promise<SectionAddendumAttachment | undefined>
}

export function SectionAddendumFields({
  addendum,
  index,
  sectionOptions,
  disabled,
  urls,
  onChange,
  onRemove,
  uploadFile,
}: Props) {
  const files = addendum.attachments ?? []
  const updateSection = (sectionId: string) =>
    onChange((current) => ({ ...current, sectionId }))
  const updateSubtitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pageSubtitle = event.target.value
    onChange((current) => ({ ...current, pageSubtitle }))
  }
  const updateCount = (count: number | null) =>
    onChange((current) => ({ ...current, addedPageCount: count ?? 1 }))
  const updateMode = (event: RadioChangeEvent) => {
    const mode = event.target.value === 'attachments' ? 'attachments' : 'blank'
    onChange((current) => ({ ...current, mode }))
  }
  const upload = async (file: File) => {
    const attachment = await uploadFile(file)
    if (attachment) {
      onChange((current) => ({
        ...current,
        attachments: [...(current.attachments ?? []), attachment],
      }))
    }
    return Upload.LIST_IGNORE
  }
  const replace = (id: string, replacement: SectionAddendumAttachment) =>
    onChange((current) => ({
      ...current,
      attachments: current.attachments?.map((file) =>
        file.id === id ? replacement : file
      ),
    }))
  const caption = (id: string, caption: string) =>
    onChange((current) => ({
      ...current,
      attachments: current.attachments?.map((file) =>
        file.id === id ? { ...file, caption } : file
      ),
    }))
  const remove = (id: string) =>
    onChange((current) => ({
      ...current,
      attachments: current.attachments?.filter((file) => file.id !== id),
    }))
  const move = (id: string, direction: -1 | 1) =>
    onChange((current) => {
      const attachments = [...(current.attachments ?? [])]
      const from = attachments.findIndex((file) => file.id === id)
      const to = from + direction
      if (from < 0 || to < 0 || to >= attachments.length) return current
      const [file] = attachments.splice(from, 1)
      attachments.splice(to, 0, file)
      return { ...current, attachments }
    })
  const renderedFiles = files.map((file, fileIndex) => (
    <AddendumAttachmentRow
      key={file.id}
      attachment={file}
      url={urls.get(file.path)}
      disabled={disabled}
      canMoveUp={fileIndex > 0}
      canMoveDown={fileIndex < files.length - 1}
      onMove={move}
      onRemove={remove}
      onCaption={caption}
      onReplace={replace}
      uploadFile={uploadFile}
    />
  ))

  return (
    <Container
      size='small'
      title={`Addendum ${index + 1}`}
      extra={
        <Popconfirm
          title='Remove this addendum?'
          description='Save to apply this change.'
          onConfirm={onRemove}
          disabled={disabled}>
          <Button danger disabled={disabled}>
            Remove addendum
          </Button>
        </Popconfirm>
      }>
      <Field>
        Section
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          aria-label={`Section for addendum ${index + 1}`}
          options={sectionOptions}
          value={addendum.sectionId}
          onChange={updateSection}
          placeholder='Select a section or enter a custom section name'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
          disabled={disabled}
        />
      </Field>
      <Field>
        Subtitle
        <Input
          value={addendum.pageSubtitle}
          onChange={updateSubtitle}
          placeholder='Optional subtitle for these pages'
          maxLength={160}
          disabled={disabled}
        />
      </Field>
      <Space orientation='vertical' size='middle'>
        <Radio.Group
          value={addendum.mode ?? 'blank'}
          disabled={disabled}
          onChange={updateMode}
          aria-label={`Content for addendum ${index + 1}`}>
          <Radio value='attachments'>Attachments</Radio>
          <Radio value='blank'>Blank pages</Radio>
        </Radio.Group>
        {addendum.mode === 'attachments' ? (
          <div>
            {renderedFiles}
            <Upload
              accept='.png,.jpg,.jpeg,.pdf'
              showUploadList={false}
              beforeUpload={upload}
              disabled={disabled}>
              <Button icon={<PlusOutlined />} disabled={disabled}>
                Add image or PDF
              </Button>
            </Upload>
            <Typography.Paragraph type='secondary'>
              Images: up to 5 MB. PDFs: up to 20 MB and 100 pages. Each image
              and PDF page becomes one report page.
            </Typography.Paragraph>
            <Typography.Text>
              {files.reduce(
                (count, file) =>
                  count + (file.kind === 'pdf' ? file.pageCount : 1),
                0
              )}{' '}
              report pages
            </Typography.Text>
          </div>
        ) : (
          <Field>
            Number of blank pages
            <InputNumber
              min={1}
              precision={0}
              value={addendum.addedPageCount}
              onChange={updateCount}
              disabled={disabled}
            />
          </Field>
        )}
      </Space>
    </Container>
  )
}
